import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import BackgroundSelector from '../components/BackgroundSelector'
import Button from '../components/Button'
import PlantList from '../components/garden/PlantList'
import SeedInventory from '../components/garden/SeedInventory'
import InlineAuthForm from '../components/InlineAuthForm'
import Modal from '../components/Modal'
import MultiPlantCanvas from '../components/MultiPlantCanvas'
import SaveProgressPrompt from '../components/SaveProgressPrompt'
import { useGardenActions } from '../hooks/useGardenActions'
import { useGardenUpdates } from '../hooks/useGardenUpdates'
import { useI18n } from '../i18n/I18nProvider'
import { supabase } from '../lib/supabase'
import { useAuthStore } from '../store/authStore'
import { useGameStore } from '../store/gameStore'

function MultiGarden() {
  // Store state
  const plants = useGameStore((state) => state.plants)
  const seedInventory = useGameStore((state) => state.seedInventory)
  const ownedBackgrounds = useGameStore((state) => state.ownedBackgrounds)
  const selectedBackground = useGameStore((state) => state.selectedBackground)
  const plantSeedAt = useGameStore((state) => state.plantSeedAt)
  const destroyPlantById = useGameStore((state) => state.destroyPlantById)
  const waterLevel = useGameStore((state) => state.waterLevel)
  const waterCooldownUntil = useGameStore((state) => state.waterCooldownUntil)
  const getWaterCooldownRemaining = useGameStore((state) => state.getWaterCooldownRemaining)
  const checkWaterRefill = useGameStore((state) => state.checkWaterRefill)
  const hydrateWaterCooldown = useGameStore((state) => state.hydrateWaterCooldown)
  const updateAllPlantsSeedProgress = useGameStore((state) => state.updateAllPlantsSeedProgress)
  const collectSeedFromPlant = useGameStore((state) => state.collectSeedFromPlant)
  const autoGardenerOwned = useGameStore((state) => state.autoGardenerOwned)
  const autoGardenerLastTick = useGameStore((state) => state.autoGardenerLastTick)
  const autoGardenerConfig = useGameStore((state) => state.autoGardenerConfig)
  const runAutoGardenerTick = useGameStore((state) => state.runAutoGardenerTick)
  const saveGame = useGameStore((state) => state.saveGame)
  
  // Récupérer l'utilisateur du store avec une valeur par défaut
  const user = useAuthStore((state) => state.user) || useAuthStore.getState().user;
  
  const navigate = useNavigate()
  const { t } = useI18n()
  
  // Local state
  const [pendingSeedType, setPendingSeedType] = useState(null)

  // Le jardin du joueur connecté est maintenant chargé globalement dans App.jsx

  useEffect(() => {
    // S'assurer que le cooldown est bien reconstitué après un rechargement
    hydrateWaterCooldown()
  }, [hydrateWaterCooldown])

  useEffect(() => {
    if (!user || typeof saveGame !== 'function') {
      return
    }

    const intervalId = setInterval(() => {
      saveGame().catch((error) => {
        console.error('Auto-save error:', error)
      })
    }, 2000)

    return () => {
      clearInterval(intervalId)
    }
  }, [user, saveGame])
  
  // Custom hooks
  const {
    showPublishModal,
    showDestroyModal,
    publishError,
    isPublishing,
    publishSnapshot,
    handleOpenPublishModal,
    handlePublishPlant,
    handleOpenDestroyModal,
    handleDestroyPlant,
    setShowPublishModal,
    setShowDestroyModal,
    setPublishSnapshot,
  } = useGardenActions()
  
  useGardenUpdates(checkWaterRefill, updateAllPlantsSeedProgress, runAutoGardenerTick)
  
  // Handlers
  const handleSelectSeed = (seedType) => {
    setPendingSeedType(seedType)
  }

  const handleSeedPlaced = (position, seedTypeFromCanvas) => {
    const seedType = seedTypeFromCanvas || pendingSeedType
    if (seedType) {
      // La graine a déjà été soustraite dans le canvas, on crée juste la plante
      const config = useGameStore.getState().config
      if (!config) return
      
      const plantConfig = config.plants[seedType]
      const newPlant = {
        id: `plant_${Date.now()}_${Math.random()}`,
        type: seedType,
        config: plantConfig,
        plantedAt: Date.now(),
        position: position,
        progress: 0,
        stage: 0,
        branches: [],
        lastSeedGeneration: Date.now(),
        pendingSeed: false,
        seedProgress: 0,
        seedClickBonus: 100,
      }
      
      useGameStore.setState((state) => ({
        plants: [...state.plants, newPlant],
        selectedPlantId: newPlant.id,
      }))
      
      setPendingSeedType(null)
    }
  }

  const handleCancelPlacement = () => {
    setPendingSeedType(null)
  }

  const handleClosePublishModal = () => {
    setShowPublishModal(false)
    setPublishSnapshot(null)
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
      <SaveProgressPrompt />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-green-800 text-center">
            {t('multiGarden.headerTitle')}
          </h1>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-start lg:gap-6">
          {/* Canvas */}
          <div className="mb-6 lg:mb-0 lg:flex-1">
            <MultiPlantCanvas 
              pendingSeedType={pendingSeedType}
              onSeedPlaced={handleSeedPlaced}
              onCancelSeed={handleCancelPlacement}
              onRequestPublish={handleOpenPublishModal}
              autoGardenerOwned={autoGardenerOwned}
              autoGardenerLastTick={autoGardenerLastTick}
              autoGardenerConfig={autoGardenerConfig}
            />
          </div>

          {/* Right side panels */}
          <div className="lg:w-[360px] w-full">
            <div className="space-y-6">
              <SeedInventory
                seedInventory={seedInventory}
                pendingSeedType={pendingSeedType}
                onSelectSeed={handleSelectSeed}
                onCancelSeed={handleCancelPlacement}
              />

              <PlantList
                plants={plants}
                onDestroy={handleOpenDestroyModal}
                onCollectSeed={collectSeedFromPlant}
              />

              <BackgroundSelector />
            </div>
          </div>
        </div>
      </div>
      {/* Publish Modal */}
      <Modal
        isOpen={showPublishModal}
        onClose={handleClosePublishModal}
        title={t('multiGarden.publishModalTitle')}
      >
        <div className="mb-6">
          <p className="mb-4">
            {t('multiGarden.publishModalBody')}
          </p>
          {publishSnapshot ? (
            <div className="mb-4 rounded-2xl bg-gradient-to-b from-sky-200 to-green-100 p-4 shadow-inner">
              <p className="text-sm font-semibold text-gray-700 mb-2">
                {t('multiGarden.publishPreviewLabel', 'Preview of your shared garden')}
              </p>
              <img
                src={publishSnapshot}
                alt="Garden preview"
                className="w-full rounded-xl border border-white shadow-lg"
              />
            </div>
          ) : (
            <div className="mb-4 p-4 rounded-2xl bg-gray-50 border border-dashed border-gray-300 text-sm text-gray-500">
              {t('multiGarden.publishPreviewPlaceholder', 'A screenshot will appear here when you press the camera button in the garden.')}
            </div>
          )}
          {!user && (
            <InlineAuthForm
              initialMode="signup"
              shouldSave={true}
              onSuccess={() => {
                handleClosePublishModal()
              }}
            />
          )}
        </div>
        {publishError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {publishError}
          </div>
        )}
        <div className="flex gap-4">
          <Button
            onClick={handlePublishPlant}
            className="flex-1"
            disabled={isPublishing || !user}
          >
            {isPublishing ? t('multiGarden.publishButtonLoading') : t('multiGarden.publishButton')}
          </Button>
          <Button
            onClick={() => setShowPublishModal(false)}
            variant="secondary"
            className="flex-1"
            disabled={isPublishing}
          >
            {t('multiGarden.cancelButton')}
          </Button>
        </div>
      </Modal>

      {/* Destroy Modal */}
      <Modal
        isOpen={showDestroyModal}
        onClose={() => setShowDestroyModal(false)}
        title={t('multiGarden.destroyModalTitle')}
        size="sm"
      >
        <p className="mb-6">
          {t('multiGarden.destroyModalBody')}
        </p>
        <div className="flex gap-4">
          <Button
            onClick={() => handleDestroyPlant(destroyPlantById)}
            variant="danger"
            className="flex-1"
          >
            {t('multiGarden.destroyButton')}
          </Button>
          <Button
            onClick={() => setShowDestroyModal(false)}
            variant="secondary"
            className="flex-1"
          >
            {t('multiGarden.cancelButton')}
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default MultiGarden
